import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { createNewTag, removeProductTagId } from '@services/collectionService';

import { TAG_COLOR_LIST } from '@/constants/constant';

import CommonImage from '@/components/CommonImage';

const ProductTags = ({
	productId,
	currentProductTagList = [],
	allTagList,
	fetchAllTags,
	updateTagList,
	setShowTagModal,
	onUpdateProjectTag,
	createTagParams = '',
	isProjectPage = false,
	projectId = null,
	onSuccess = () => {}
}) => {
	const [activeColorCode, setActiveColorCode] = useState('#DBFFE5');
	const [text, setText] = useState('');
	const dispatch = useDispatch();

	const renderUnusedTags = () => {
		let usedTagIds = currentProductTagList?.map((item) => item.id);
		if (usedTagIds) {
			return allTagList?.filter((item) => !usedTagIds.includes(item.id));
		} else {
			return allTagList;
		}
	};

	const debouncedFetchTags = useCallback(
		debounce(async (searchText) => {
			await fetchAllTags(searchText);
		}, 300),
		[]
	);

	useEffect(() => {
		debouncedFetchTags(text);
		return () => {
			debouncedFetchTags.cancel();
		};
	}, [text, debouncedFetchTags]);

	const createTag = async (id) => {
		try {
			dispatch(openLoader());
			let postData = { text, colorCode: activeColorCode };
			if (id) {
				postData['id'] = id;
			}

			const projectPageBody = {
				text,
				colorCode: activeColorCode,
				projectId: parseInt(projectId),
				projectScope: 'PROJECT_ID'
			};

			if (isProjectPage) {
				onUpdateProjectTag(productId, projectPageBody);
			} else {
				const data = await createNewTag(productId, [postData], createTagParams);
				updateTagList(data);
			}
			setText('');
			setShowTagModal && setShowTagModal(false);
			dispatch(closeLoader());
			onSuccess && onSuccess();
		} catch {
			dispatch(closeLoader());
		}
	};

	const isActiveColor = (code) => (activeColorCode === code ? 'single-color active' : 'single-color');

	const toggleActiveColor = (code) => {
		setActiveColorCode(code);
	};

	const createAbleTags = () => {
		if (allTagList?.length === 0 && text !== '') {
			return (
				<div className="create">
					<p className="px-3 py-1 d-flex align-items-center" onClick={() => createTag()}>
						<span className="semibold-14">Create</span>
						<span
							className="status radius without-nowrap ml-2"
							style={{ background: activeColorCode }}
						>
							{text}
						</span>
					</p>
					<div className="colors">
						{TAG_COLOR_LIST?.map((item) => (
							<span
								key={`tag_color_${item}`}
								className={`single-color ${isActiveColor(item)}`}
								onClick={() => toggleActiveColor(item)}
								style={{ background: item }}
							/>
						))}
					</div>
				</div>
			);
		}
	};

	const removeTag = async (id) => {
		try {
			dispatch(openLoader());
			const data = await removeProductTagId(productId, id);
			updateTagList(data);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const renderExistingTags = () => {
		return currentProductTagList?.map((item, index) => {
			return (
				<span className="status radius" key={`tag_${index}`} style={{ background: item.colorCode }}>
					{item.text}{' '}
					{!isProjectPage && (
						<CommonImage
							onClick={() => removeTag(item.id)}
							src={`/icons/chip-close.svg`}
							className="ml-1"
						/>
					)}
				</span>
			);
		});
	};

	const listItems = isProjectPage ? allTagList : renderUnusedTags(allTagList) || [];

	const renderAllTags = () => {
		return listItems?.map((item, index) => {
			return (
				<p
					className={`px-3 py-1 d-flex align-items-center ${index === 0 ? 'active' : ''}`}
					key={`all_tags_${index}`}
					onClick={() => {
						if (onUpdateProjectTag) {
							onUpdateProjectTag(productId, item);
						} else {
							createTag(item.id);
							setShowTagModal && setShowTagModal(false);
						}
					}}
				>
					<span className="status radius cursor-pointer" style={{ background: item.colorCode }}>
						{item.text}
					</span>
				</p>
			);
		});
	};

	return (
		<div className="select-tag-dropdown">
			<div className="py-2 px-3">
				<div className="selected-tags design-tags mb-2">{!isProjectPage && renderExistingTags()}</div>
				<div className="input-tag">
					<input
						type="text"
						autoComplete="off"
						autoFocus
						placeholder="Select or create new"
						value={text}
						onChange={(e) => setText(e.target.value)}
						onKeyDown={(event) => {
							if (
								renderUnusedTags(allTagList)?.length === 0 &&
								text !== '' &&
								event.key === 'Enter'
							) {
								createTag();
							} else if (event.key === 'Enter') {
								createTag(renderUnusedTags()?.length > 0 && renderUnusedTags()?.[0]?.id);
							}
						}}
					/>
					<CommonImage src={`/images/chevron_down.svg`} />
				</div>
			</div>
			{createAbleTags()}
			<div className="suggested-tags scroll-y-label mt-1">{renderAllTags()}</div>
		</div>
	);
};

export default ProductTags;
